/* Shared primitives: oven-timer ring (static + live countdown), move arrow,
   PIN cells, and the mobile phone frame. */
import { useEffect, useState, type ReactNode } from "react";

/** Re-render ~4x/sec while `active`, for live countdowns. */
function useTick(active: boolean): number {
  const [, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    const id = setInterval(() => setN((n) => n + 1), 250);
    return () => clearInterval(id);
  }, [active]);
  return Date.now();
}

export function OvenTimer({
  size = 116,
  stroke = 9,
  color = "var(--red)",
  seconds,
  endsAt,
  timeLimit,
}: {
  size?: number;
  stroke?: number;
  color?: string;
  /** Static label (design screens). */
  seconds?: string;
  /** Live countdown: epoch ms the timer ends + total seconds for the ring. */
  endsAt?: number;
  timeLimit?: number;
}) {
  const live = endsAt != null && timeLimit != null;
  const now = useTick(live);
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;

  let frac = 1; // portion of ring filled (remaining)
  let label = seconds ?? "";
  if (live) {
    const remainingMs = Math.max(0, endsAt! - now);
    const remaining = remainingMs / 1000;
    frac = Math.min(1, Math.max(0, remaining / timeLimit!));
    label = Math.ceil(remaining).toString();
  }
  const offset = c * (1 - frac);
  const danger = live && frac < 0.25;

  return (
    <div className="bk-timer" style={{ width: size, height: size }}>
      <svg className="bk-timer__ring" width={size} height={size}>
        <circle className="bk-timer__track" cx={size / 2} cy={size / 2} r={r} strokeWidth={stroke} />
        <circle
          className="bk-timer__fill"
          cx={size / 2}
          cy={size / 2}
          r={r}
          strokeWidth={stroke}
          style={{
            stroke: danger ? "var(--red-bright)" : color,
            strokeDasharray: c,
            strokeDashoffset: live ? offset : 0,
            transition: live ? "stroke-dashoffset .25s linear, stroke .2s" : undefined,
          }}
        />
      </svg>
      <span className="bk-timer__num" style={{ fontSize: size * 0.3 }}>
        {label}
      </span>
    </div>
  );
}

export function MoveArrow({ dir }: { dir: "up" | "down" | "same" }) {
  if (dir === "same") return <span style={{ color: "var(--ink-faint)", fontSize: 13 }}>—</span>;
  const up = dir === "up";
  return (
    <span
      style={{
        color: up ? "var(--good)" : "var(--bad)",
        display: "inline-flex",
        alignItems: "center",
        fontWeight: 800,
        fontSize: 12,
      }}
    >
      <svg width="13" height="13" viewBox="0 0 12 12" style={{ transform: up ? "none" : "rotate(180deg)" }}>
        <path d="M6 2 10 8 2 8Z" fill="currentColor" />
      </svg>
    </span>
  );
}

export function PinCells({ value }: { value: string }) {
  const cells = value.padEnd(6, " ").slice(0, 6).split("");
  const filled = value.length;
  return (
    <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
      {cells.map((d, i) => {
        const isCursor = i === Math.min(filled, 5);
        return (
          <div
            key={i}
            style={{
              width: 46,
              height: 58,
              borderRadius: 13,
              background: "var(--surface)",
              border: `2px solid ${isCursor ? "var(--red)" : "var(--line)"}`,
              boxShadow: isCursor ? "0 0 0 4px var(--red-tint)" : "var(--sh-sm)",
              display: "grid",
              placeItems: "center",
            }}
          >
            <span className="mono" style={{ fontSize: 28, fontWeight: 700, color: "var(--ink)" }}>
              {d.trim()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

export function StatusBar({ dark = false }: { dark?: boolean }) {
  const c = dark ? "rgba(255,255,255,.95)" : "var(--ink)";
  return (
    <div
      style={{
        height: 44,
        flex: "0 0 44px",
        display: "flex",
        alignItems: "center",
        padding: "0 24px",
        color: c,
        fontWeight: 700,
        fontSize: 14,
      }}
    >
      <span className="mono">9:41</span>
      <div style={{ flex: 1 }} />
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <svg width="17" height="12" viewBox="0 0 17 12" fill={c}>
          <rect x="0" y="7" width="3" height="5" rx="1" />
          <rect x="4.5" y="4.5" width="3" height="7.5" rx="1" />
          <rect x="9" y="2" width="3" height="10" rx="1" />
          <rect x="13.5" y="0" width="3" height="12" rx="1" opacity=".35" />
        </svg>
        <svg width="22" height="12" viewBox="0 0 22 12" fill="none">
          <rect x="1" y="1" width="18" height="10" rx="3" stroke={c} strokeWidth="1.3" opacity=".5" />
          <rect x="3" y="3" width="12" height="6" rx="1.5" fill={c} />
          <rect x="20" y="4" width="1.5" height="4" rx="1" fill={c} opacity=".5" />
        </svg>
      </div>
    </div>
  );
}

/** Mobile player frame — fills the viewport on phones, framed on desktop. */
export function Phone({
  children,
  bg = "var(--cream)",
  dark = false,
  grain = true,
}: {
  children: ReactNode;
  bg?: string;
  dark?: boolean;
  grain?: boolean;
}) {
  return (
    <div
      className={"bk pat bk-phoneframe" + (grain ? " bk-grain" : "")}
      style={{ background: bg, display: "flex", flexDirection: "column", height: "auto", minHeight: "100dvh" }}
    >
      <div style={{ flex: 1, display: "flex", flexDirection: "column", position: "relative", zIndex: 1, minHeight: 0 }}>
        {children}
      </div>
    </div>
  );
}

/** Full-viewport stage for the host/TV + admin screens. An optional `bg` (a CSS
    `background` value) paints a full-bleed layer behind the content; the screens
    on top go transparent (see `.bk-stage--bg` in bakeoff.css) so it shows through. */
export function Stage({ children, bg }: { children: ReactNode; bg?: string | null }) {
  return (
    <div className={`bk-stage pat${bg ? " bk-stage--bg" : ""}`}>
      {bg && <div className="bk-bg-layer" aria-hidden style={{ background: bg }} />}
      {children}
    </div>
  );
}
